import { hasAnyRole, isWallDisplayOnly, OperationsPrincipals, SchedulePlanners } from "../../auth/roles";
import { commandsForRoles } from "../command/CommandPalette";
import { navFor } from "../hub/hubs";
import { matchesWords, type RecentItem } from "./searchModel";
import { homeGoToItems } from "../home/homeModel";

// Release 1.5.0 "Go to" (Ctrl+P / Ctrl+Shift+P): every page and tab the person may open (the menu's own rule, so the
// Purchaser's and the read-only Executive's lists are respected), the floor commands, a few actions, and the records
// opened recently in this browser. Pure, so the filtering is tested without a screen.

export type GoToActionId = "rebalance" | "ship-date" | "pilot-feedback" | "reduce-motion" | "notifications" | "print" | "shortcuts" | "tour";

export interface GoToItem {
  id: string;
  label: string;
  group: string;
  detail: string | null;
  /** A page to open, or an action to run. */
  to?: string;
  action?: GoToActionId;
  keywords: string;
  shortcut?: string;
}

export interface GoToContext {
  /** The person's own "Reduce motion" choice is on (the label says what the action will do). */
  reduceMotionOn: boolean;
  recent: readonly RecentItem[];
}

/** The actions this person may run. Each is gated by the same rule as the screen or API behind it. */
export function actionsFor(roles: readonly string[], ctx: Pick<GoToContext, "reduceMotionOn">): GoToItem[] {
  if (roles.length === 0 || isWallDisplayOnly(roles)) return [];
  const planner = hasAnyRole(roles, SchedulePlanners);
  const principal = hasAnyRole(roles, OperationsPrincipals);
  const screens = new Set(navFor(roles).flatMap((s) => s.items.map((i) => i.to)));
  const items: GoToItem[] = [];
  if (planner && screens.has("/planning/scenarios")) {
    items.push({ id: "action:rebalance", label: "Rebalance the schedule", group: "Actions", detail: "Scenario lab — VIPER proposes, you decide", to: "/planning/scenarios", keywords: "rebalance scenario what-if move", shortcut: "R" });
    items.push({ id: "action:ship-date", label: "Ship date? (capable to promise)", group: "Actions", detail: "When could a new order finish — computed from the finite schedule", action: "ship-date", keywords: "ship date ctp capable promise when can we ship" });
  }
  if (principal) {
    // D-O115: the guided tour, with the part for the person's role, is always one Go to away.
    items.push({ id: "action:tour", label: "Take the tour", group: "Actions", detail: "A short walk around Summit Operations, with a part for your role", action: "tour", keywords: "tour guide help start learn how welcome" });
    items.push({ id: "action:pilot-feedback", label: "Start pilot feedback", group: "Actions", detail: "Tell the owner what works and what does not", action: "pilot-feedback", keywords: "pilot feedback report problem idea" });
    items.push({
      id: "action:reduce-motion",
      label: ctx.reduceMotionOn ? "Turn animation back on" : "Reduce motion",
      group: "Actions",
      detail: "Your choice, on this computer",
      action: "reduce-motion",
      keywords: "reduce motion animation accessibility toggle"
    });
  }
  if (screens.has("/my/notifications")) {
    items.push({ id: "action:notifications", label: "Open My notifications", group: "Actions", detail: null, to: "/my/notifications", keywords: "notifications messages bell" });
  }
  items.push({ id: "action:print", label: "Print this page", group: "Actions", detail: "Opens the browser's print", action: "print", keywords: "print paper pdf" });
  items.push({ id: "action:shortcuts", label: "Keyboard shortcuts", group: "Actions", detail: null, action: "shortcuts", keywords: "keyboard shortcuts keys help", shortcut: "?" });
  return items;
}

/** Everything in the Go to list for this person: recent records, actions, floor commands, then every permitted page. */
export function buildGoToItems(roles: readonly string[], ctx: GoToContext): GoToItem[] {
  if (roles.length === 0 || isWallDisplayOnly(roles)) return [];
  const items: GoToItem[] = [];
  const seen = new Set<string>();
  const add = (item: GoToItem) => {
    const k = item.to ?? item.id;
    if (seen.has(k)) return;
    seen.add(k);
    items.push(item);
  };
  for (const r of ctx.recent) add({ id: `recent:${r.to}`, label: r.label, group: "Recent", detail: r.detail ?? null, to: r.to, keywords: r.label });
  for (const a of actionsFor(roles, ctx)) add(a);
  for (const h of homeGoToItems(roles)) add(h);
  const screens = navFor(roles).flatMap((s) => s.items.map((i) => i.to));
  for (const c of commandsForRoles(roles)) {
    // Only the floor commands themselves: the pages come from the menu below, under their own hub.
    if (!c.id.startsWith("cmd-")) continue;
    // The menu's own rule applies to the floor commands too (a Purchaser's list has no Kanban).
    if (!screens.some((s) => c.to === s || c.to.startsWith(`${s}/`))) continue;
    add({ id: c.id, label: c.label, group: "Floor & Planning", detail: null, to: c.to, keywords: c.keywords, shortcut: c.shortcut === "G" ? "g g" : c.shortcut });
  }
  for (const section of navFor(roles)) {
    for (const item of section.items) add({ id: `page:${item.id}`, label: item.label, group: section.heading, detail: section.heading, to: item.to, keywords: `${item.label} ${section.heading}` });
  }
  return items;
}

/** Word-AND filtering over label, group and keywords; labels that start with the query come first. */
export function filterGoTo(items: readonly GoToItem[], query: string): GoToItem[] {
  const q = query.trim();
  if (!q) return [...items];
  const hits = items.filter((i) => matchesWords(`${i.label} ${i.group} ${i.keywords} ${i.detail ?? ""}`, q));
  const lower = q.toLowerCase();
  return [...hits.filter((i) => i.label.toLowerCase().startsWith(lower)), ...hits.filter((i) => !i.label.toLowerCase().startsWith(lower))];
}

/** Ctrl+P is left to the browser's print on the pages whose whole point is printing (the work-order router). */
export function ctrlPPrints(pathname: string): boolean {
  return /^\/purchasing\/requests\/[^/]+\/router\/?$/.test(pathname);
}
